// Mirrors impire-sdk-go/inference/wire.go — what the Models surface speaks of
// the inference family (02-DESIGN/05-inference/01-models-and-instances.md;
// decision 0011): the service name every instance registers under, the
// metadata discovery shows, the name's grammar and the credential's vault
// path, re-exported from the library; and the settings document of § the
// settings document, which no Go file defines because the instance reads it
// as a file — its field names are the design's table, and the console shows
// them beside the document as a hint and not a schema. The console runs no
// inference: nothing here speaks a capability.

import { inference } from "@impire-io/sdk";

/** SERVICE is the micro service name every instance registers under, so one scatter finds them all. */
export const SERVICE = inference.SERVICE;

/** Instance is one inference instance as discovery shows it. */
export type Instance = inference.Instance;

/** Capability is one of the three things an instance may serve. */
export const Capability = inference.Capability;
export type Capability = inference.Capability;

/** CAPABILITIES lists the three. */
export const CAPABILITIES = inference.CAPABILITIES;

/** MAX_NAME bounds the tenant's name for a model. */
export const MAX_NAME = inference.MAX_NAME;

/** validName reports whether s is a name a tenant may give a model: `[A-Za-z0-9._-]+`, at most MAX_NAME characters. */
export const validName = inference.validName;

/** credentialPath is `workload.<id>.api-key`, where the admin stores the provider's key and the instance alone reads it. */
export const credentialPath = inference.credentialPath;

/** discover scatters `$SRV.INFO.inference` on the caller's own connection: what the tenant has is what runs now. */
export const discover = inference.discover;

/** Provider is an adapter the inference image ships (§ providers). */
export const Provider = {
  openai: "openai",
  anthropic: "anthropic",
  jev: "jev",
} as const;

export type Provider = (typeof Provider)[keyof typeof Provider];

/** PROVIDERS lists the three that ship first. */
export const PROVIDERS: readonly Provider[] = Object.values(Provider);

/**
 * PROVIDER_HOSTS is the host each adapter reaches when the settings name no
 * endpoint: what the registration's egress admits. Jev names none here: its
 * endpoint is the settings' `base-url`, always.
 */
export const PROVIDER_HOSTS: Readonly<Record<Provider, string | undefined>> = {
  openai: "api.openai.com",
  anthropic: "api.anthropic.com",
  jev: undefined,
};

/** ANTHROPIC_CLIENT_PREFIXES are what an Anthropic-API client keeps in its picker: a model id beginning with one of these (§ names). */
export const ANTHROPIC_CLIENT_PREFIXES: readonly string[] = ["claude", "anthropic"];

/** passesAnthropicFilter reports whether a name survives an Anthropic-API client's picker. */
export function passesAnthropicFilter(name: string): boolean {
  return ANTHROPIC_CLIENT_PREFIXES.some((p) => name.toLowerCase().startsWith(p));
}

/**
 * Settings is the one JSON document, `settings`, an inference instance reads
 * and validates as it would a file (§ the settings document). Field names are
 * the design's.
 */
export interface Settings {
  /** The adapter: openai, anthropic, jev, and whatever a later module registers. */
  provider: string;
  /** The provider's own model identifier, sent to the provider verbatim. */
  model: string;
  /** The tenant's name for the model: what apps and workloads ask for. */
  name: string;
  /** What a person reads in the console and a model picker; defaults to name. */
  "display-name"?: string;
  /** The provider's endpoint when it is not the provider's default; its host must be on the registration's egress. */
  "base-url"?: string;
  /** Parameters applied when a request names none. */
  defaults?: Record<string, unknown>;
  /** The subset of chat, embed and decide this instance serves; defaults to what the adapter knows. */
  capabilities?: string[];
}

/** SETTINGS_FIELDS is the field list the console shows beside a model's settings, as a hint and not a schema. */
export const SETTINGS_FIELDS: readonly { field: keyof Settings; what: string }[] = [
  { field: "provider", what: "the provider: openai, anthropic or jev" },
  { field: "model", what: "the provider's own model id, sent verbatim" },
  { field: "name", what: "what apps ask for; letters, digits, dot, dash, underscore" },
  { field: "display-name", what: "what people read; the name when absent" },
  { field: "base-url", what: "the endpoint, only where it is not the provider's own" },
  { field: "defaults", what: "parameters applied when a request names none" },
  { field: "capabilities", what: "chat, embed, decide; what the provider's model can do when absent" },
];

/** parseSettings reads a settings document; undefined when it is not one. */
export function parseSettings(text: string): Settings | undefined {
  let v: unknown;
  try {
    v = JSON.parse(text);
  } catch {
    return undefined;
  }
  if (typeof v !== "object" || v === null) {
    return undefined;
  }
  const o = v as Record<string, unknown>;
  if (typeof o.provider !== "string" || typeof o.model !== "string" || typeof o.name !== "string") {
    return undefined;
  }
  return o as unknown as Settings;
}

/** settingsText renders a settings document as the console writes it: the fields in the design's order, absent ones left out. */
export function settingsText(s: Settings): string {
  const out: Record<string, unknown> = { provider: s.provider, model: s.model, name: s.name };
  if (s["display-name"] !== undefined && s["display-name"] !== "") {
    out["display-name"] = s["display-name"];
  }
  if (s["base-url"] !== undefined && s["base-url"] !== "") {
    out["base-url"] = s["base-url"];
  }
  if (s.defaults !== undefined && Object.keys(s.defaults).length > 0) {
    out.defaults = s.defaults;
  }
  if (s.capabilities !== undefined && s.capabilities.length > 0) {
    out.capabilities = s.capabilities;
  }
  return JSON.stringify(out, null, 2) + "\n";
}

/**
 * egressHost is the host an instance reaches, from the settings' endpoint when
 * one is named and the provider's default otherwise; undefined when neither
 * names one, which is a settings document the instance would refuse.
 */
export function egressHost(
  provider: string,
  baseURL: string | undefined,
): { host: string; port: number } | undefined {
  if (baseURL !== undefined && baseURL !== "") {
    try {
      const u = new URL(baseURL);
      const port = u.port !== "" ? Number(u.port) : u.protocol === "http:" ? 80 : 443;
      return u.hostname === "" ? undefined : { host: u.hostname, port };
    } catch {
      return undefined;
    }
  }
  const host = (PROVIDER_HOSTS as Record<string, string | undefined>)[provider];
  return host === undefined ? undefined : { host, port: 443 };
}

// The content callout (02-the-inference-contract.md § the content callout;
// 01-models-and-instances.md § the required document; decision 0040), as
// impire-sdk-go/inference/callout.go carries it. The console runs no callout:
// it shows the block a model's required document carries and whether a
// callout service of that name answers discovery.

/** CALLOUT_SERVICE is the micro service name a callout service registers under, its registration name in META_CALLOUT_NAME. */
export const CALLOUT_SERVICE = "callout";

/** META_CALLOUT_NAME is the metadata key a callout service advertises its registration name under. */
export const META_CALLOUT_NAME = "callout.name";

/** HEADER_CALLOUT rides every reply of an instance whose required document names a callout: the name, then the count of findings by kind. */
export const HEADER_CALLOUT = "Impire-Callout";

/** CODE_CALLOUT_UNAVAILABLE refuses a request whose required callout did not answer; HEADER_CALLOUT names it. */
export const CODE_CALLOUT_UNAVAILABLE = "callout-unavailable";

/** FIELD_CALLOUT is the one block of the required document with no settings counterpart. */
export const FIELD_CALLOUT = "callout";

/** Scope is what the callout is sent: every message the model will read, or only the turn not yet answered. */
export const Scope = {
  whole: "whole",
  new: "new",
} as const;

export type Scope = (typeof Scope)[keyof typeof Scope];

/** DEFAULT_CALLOUT_DEADLINE is the deadline a requirement leaves unnamed. */
export const DEFAULT_CALLOUT_DEADLINE = "5s";

/** CalloutRequirement is the required document's `callout` block: the callout's registration name, the scope (whole by default), the deadline (5s by default). */
export interface CalloutRequirement {
  name: string;
  /** whole or new; any other word is the document's, shown as written, and the instance refuses it. */
  scope?: string;
  /** A Go duration. */
  deadline?: string;
}

/**
 * calloutOf reads the `callout` block of a required document; undefined when
 * the document is not JSON, names no callout, or the block has no name — a
 * block the instance would refuse is still shown by its name where it has one.
 */
export function calloutOf(required: string | undefined): CalloutRequirement | undefined {
  if (required === undefined) {
    return undefined;
  }
  let v: unknown;
  try {
    v = JSON.parse(required);
  } catch {
    return undefined;
  }
  if (typeof v !== "object" || v === null) {
    return undefined;
  }
  const c = (v as Record<string, unknown>)[FIELD_CALLOUT];
  if (typeof c !== "object" || c === null) {
    return undefined;
  }
  const o = c as Record<string, unknown>;
  if (typeof o.name !== "string" || o.name === "") {
    return undefined;
  }
  const out: CalloutRequirement = { name: o.name };
  if (typeof o.scope === "string" && o.scope !== "") {
    out.scope = o.scope;
  }
  if (typeof o.deadline === "string" && o.deadline !== "") {
    out.deadline = o.deadline;
  }
  return out;
}
